import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Download, Smartphone, ArrowLeft, Check } from "lucide-react";
import { useI18n } from "@/i18n";
import { getCountry } from "@/country";
import { kaynagiYakala } from "@/lib/kaynak";

/**
 * /indir — reklamdan gelen kişiyi doğru mağazaya bırakan sayfa.
 *
 * Neden var: reklamlar bugüne kadar ana sayfaya yönlendiriyordu ve
 * kullanıcı mağazayı kendi bulmak zorunda kalıyordu. Mağaza trafiği
 * kampanyasının hedefi burası olacak.
 *
 * Davranış:
 *   • iPhone / iPad  → App Store'a anında yönlendirir
 *   • Android        → Play'e anında yönlendirir
 *   • Masaüstü       → iki rozet + QR kod gösterir
 *   • Uygulama içi   → hiçbir yere gitmez, "zaten yüklü" der
 *
 * Yönlendirme yalnızca o platformun bağlantısı GERÇEKTEN varsa yapılır.
 * Bağlantı yoksa kişi boş bir mağaza sayfasına düşmez, sayfada kalır.
 */

/**
 * App Store'daki sayısal kimlik.
 * App Store Connect > uygulama > "App Information" > sağ sütun "Apple ID"
 * (SKU'nun hemen altında, 10 hane).
 *
 * K-33 kararı gereği iOS tarafında TEK uygulama kaydı var — Türkiye ve
 * uluslararası sürüm aynı kayıt — bu yüzden tek kimlik yeterli.
 *
 * Boş kaldığı sürece iOS rozeti gizlenir ve iPhone'dan gelen kişi
 * yönlendirilmez. Yanlış bir mağaza sayfasına atmaktansa hiç atmamak doğru.
 */
const APPLE_ID = "6780546360";

/**
 * Android tarafında İKİ ayrı uygulama var (K-33).
 *
 *   tr   → tr.sokakkedileri.app   — Play'de canlı
 *   intl → com.straypaw.rescue    — kapalı testte, herkese açık listesi YOK
 *
 * StrayPaw'ın Play listesi açılınca alttaki null yerine paket adı yazılacak.
 */
const ANDROID_PAKET = {
  tr: "tr.sokakkedileri.app",
  intl: null,
};

/** iPadOS 13+ kendini Mac gibi tanıtır; dokunma noktası sayısı ayırt eder. */
function platformTespit() {
  if (typeof navigator === "undefined") return "masaustu";
  const ua = navigator.userAgent || "";
  const iPadOS =
    navigator.platform === "MacIntel" && (navigator.maxTouchPoints || 0) > 1;
  if (/iPad|iPhone|iPod/.test(ua) || iPadOS) return "ios";
  if (/Android/i.test(ua)) return "android";
  return "masaustu";
}

/** Capacitor sarmalayıcısı içinde miyiz — yani kişi zaten uygulamada mı. */
function uygulamaIcinde() {
  const cap = typeof window !== "undefined" ? window.Capacitor : null;
  return !!(cap && typeof cap.isNativePlatform === "function" && cap.isNativePlatform());
}

export default function Indir() {
  const { t } = useI18n();
  const [yonlendiriliyor, setYonlendiriliyor] = useState(false);

  const icerde = uygulamaIcinde();
  const platform = useMemo(platformTespit, []);

  const iosUrl = APPLE_ID ? `https://apps.apple.com/app/id${APPLE_ID}` : null;

  const androidPaket =
    getCountry() === "tr" ? ANDROID_PAKET.tr : ANDROID_PAKET.intl;
  const androidUrl = androidPaket
    ? `https://play.google.com/store/apps/details?id=${androidPaket}`
    : null;

  const hedef =
    platform === "ios" ? iosUrl : platform === "android" ? androidUrl : null;

  // Rozetler yalnizca kisinin cihazina uyani gosterir; masaustunde ikisi de.
  // iPhone'daki birine Play rozeti gostermek yanlis yonlendirmedir.
  const iosGoster = !!iosUrl && (platform === "ios" || platform === "masaustu");
  const androidGoster =
    !!androidUrl && (platform === "android" || platform === "masaustu");

  useEffect(() => {
    // Reklam kaynağını sakla. Mağazaya çıkınca bu bilgi kaybolur —
    // uygulama kurulumu tarayıcı ile ilişkilendirilemez — ama web
    // üzerinden kaydolanlar için kaynak korunur (B.3).
    //
    // 3 Eyl 2026: burada ELLE localStorage yazılıyordu ve üç şeyi birden
    // yanlış yapıyordu:
    //   1. Zaman damgasını (`sk_signup_source_ts`) yazmıyordu. `kaynagiOku()`
    //      damgasız kaydı BAYAT sayıp SİLİYOR — yani /indir üzerinden gelen
    //      kaynak, ilk okumada sessizce çöpe gidiyordu. Tam olarak korumaya
    //      çalıştığımız veri kayboluyordu.
    //   2. "İlk kaynak kazanır" kuralını uygulamıyordu; her ziyaret üzerine
    //      yazıyordu.
    //   3. `slice(0, 40)` ile KIRPIYORDU — sunucu tarafında aynı hatayı
    //      kendi testimiz yakalamıştı: kırpma, 40 karakteri aşan iki farklı
    //      kampanyayı tek satırda toplar ve raporu sessizce yanlış yapar.
    // Tek kural tek yerde: `lib/kaynak.js`.
    kaynagiYakala();

    if (icerde || !hedef) return;
    setYonlendiriliyor(true);
    // replace: geri tuşu kişiyi bu sayfaya hapsetmesin.
    window.location.replace(hedef);
  }, [icerde, hedef]);

  if (icerde) {
    return (
      <div className="max-w-xl mx-auto px-6 py-20 text-center" data-testid="indir-page">
        <div className="w-20 h-20 rounded-full bg-green-50 grid place-items-center mx-auto">
          <Check className="text-green-600" size={36} />
        </div>
        <h1 className="font-display font-bold text-3xl tracking-tighter mt-6">
          {t("indir.zatenVar")}
        </h1>
        <Link
          to="/"
          className="inline-flex items-center gap-2 mt-8 text-orange-600 font-semibold"
        >
          <ArrowLeft size={18} />
          {t("indir.anaSayfa")}
        </Link>
      </div>
    );
  }

  const paylasimAdresi =
    typeof window !== "undefined"
      ? `${window.location.origin}/indir`
      : "https://sokakkedileri.tr/indir";

  return (
    <div className="max-w-xl mx-auto px-6 py-16 text-center" data-testid="indir-page">
      <div className="w-20 h-20 rounded-full bg-orange-50 grid place-items-center mx-auto">
        <Download className="text-orange-600" size={36} />
      </div>

      <h1 className="font-display font-bold text-4xl tracking-tighter mt-6">
        {t("indir.baslik")}
      </h1>
      <p className="text-stone-600 mt-3 leading-relaxed">{t("indir.altBaslik")}</p>

      {yonlendiriliyor && (
        <p className="text-sm text-stone-500 mt-6" data-testid="indir-yonlendirme">
          {t("indir.yonlendiriliyor")}
        </p>
      )}

      <div className="mt-10 flex flex-col gap-3">
        {iosGoster && (
          <a
            href={iosUrl}
            className="flex items-center justify-center gap-3 h-14 rounded-2xl bg-stone-900 text-white font-bold text-base"
            data-testid="indir-ios"
          >
            <Smartphone size={20} />
            {t("indir.appStore")}
          </a>
        )}

        {androidGoster && (
          <a
            href={androidUrl}
            className="flex items-center justify-center gap-3 h-14 rounded-2xl bg-stone-900 text-white font-bold text-base"
            data-testid="indir-android"
          >
            <Smartphone size={20} />
            {t("indir.playStore")}
          </a>
        )}

        {!iosGoster && !androidGoster && (
          <p className="text-stone-600" data-testid="indir-yok">
            {t("indir.yakinda")}
          </p>
        )}
      </div>

      {platform === "masaustu" && (iosUrl || androidUrl) && (
        <div className="mt-12">
          <div className="inline-block p-4 bg-white rounded-2xl border border-stone-200">
            <QRCodeSVG value={paylasimAdresi} size={148} level="M" />
          </div>
          <p className="text-sm text-stone-500 mt-3">{t("indir.qr")}</p>
        </div>
      )}

      {!androidUrl && iosUrl && (
        <p className="text-xs text-stone-500 mt-8">{t("indir.androidYakinda")}</p>
      )}

      <Link
        to="/"
        className="inline-flex items-center gap-2 mt-12 text-orange-600 font-semibold"
      >
        <ArrowLeft size={18} />
        {t("indir.anaSayfa")}
      </Link>
    </div>
  );
}
